'use client'

import AspectRatioCover from '@comp/AspectRatioCover'
import LiveCountdown from '@comp/page/common/card/live/countdown'
import useLogin from '@hook/useLogin'
import useProgressRouter from '@hook/useProgressRouter'
import useTcPlayer, { TcPlayerLayout } from '@hook/useTcPlayer'
import { eventTypes, liveStatus } from '@lib/config'
import { fetchPost } from '@lib/fetch/client'
import { openCourseRoom, openCourseTest } from '@lib/route/event'
import { Button } from '@comp/material'
import { get, map } from 'lodash-es'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useState } from 'react'
import RemindButton from '../../card/live/remindButton'
import { useSubscribeLiveContext } from '../../card/live/list/subscribe'

export default function LivePlayer({ data }) {
  const locale = useLocale()
  const ts = useTranslations('live')
  const { isLogin, user, openSigninDialog } = useLogin()

  const liveStream = get(data, 'liveStream')
  const status = get(liveStream, 'status')
  const isOpenCourse = get(data, 'eventType') === eventTypes.openCourse
  const isOwner = isLogin && get(liveStream, 'broadCaster.userId') === get(user, 'id')

  // 是否显示播放器
  const [visiblePlayer, setVisiblePlayer] = useState(false)

  const { initPlayer } = useTcPlayer({
    containerId: 'live-player',
    sources:
      status === liveStatus.living
        ? [
          { src: get(data, 'liveStream.pullUrl.m3u8', '') },
          { src: get(data, 'liveStream.pullUrl.m3u8Hd', '') },
          { src: get(data, 'liveStream.pullUrl.flv', '') },
          { src: get(data, 'liveStream.pullUrl.flvHd', '') }
        ]
        : map(get(data, 'liveStream.record', []), (item) => [
          { src: item.videoUrl, type: `video/${item.fileFormat}` }
        ]),
    options: {
      autoplay: true,
      poster: data.cover
    }
  })

  const { routerPush } = useProgressRouter()
  // 是否统计过观看
  const [isWatched, setIsWatched] = useState(false)
  // 观看直播
  const playLiveHandle = useCallback(() => {
    if (isOwner) {
      routerPush(openCourseTest(data, locale))
    } else if (isOpenCourse) {
      routerPush(openCourseRoom(data, locale))
    } else {
      setVisiblePlayer(true)
      setTimeout(() => {
        initPlayer()
      }, 0)
    }

    if (isWatched) return
    setIsWatched(true)
    fetchPost(`live/statistics/watch/${data.liveStream.id}`)
  }, [
    setVisiblePlayer,
    initPlayer,
    data,
    isWatched,
    setIsWatched,
    isOpenCourse,
    routerPush,
    isOwner,
    locale
  ])
  // 查看回放
  const playRecordHandle = useCallback(() => {
    setVisiblePlayer(true)
    setTimeout(() => {
      initPlayer()
    }, 0)
    if (isWatched) return
    setIsWatched(true)
    fetchPost(`live/statistics/play/${data.liveStream.id}`)
  }, [setVisiblePlayer, initPlayer, data, isWatched, setIsWatched])

  const isSubscribed = useSubscribeLiveContext()
  return (
    <>
      <section className="relative">
        {visiblePlayer ? (
          <div className="aspect-[800/600] w-full">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video id="live-player" width="800" height="600" preload="auto" playsInline />
          </div>
        ) : (
          <>
            <AspectRatioCover src={data.cover} width={800} height={600} alt={data.name} priority />
            <div className="absolute inset-x-0 inset-y-0 flex flex-col items-center justify-center gap-y-pix-10 bg-image-mask-sm">
              {
                // 直播未开始
                status === liveStatus.notStart
                && (isOpenCourse && isOwner ? (
                  // 立即查看
                  <Button
                    variant="contained"
                    type="button"
                    sx={{ borderRadius: '30px' }}
                    onClick={playLiveHandle}
                  >
                    {ts('viewNow')}
                  </Button>
                ) : (
                  <>
                    <div className="text-f.7 font-semibold text-white">{ts('countdown')}</div>
                    <div className="text-f1.4 font-semibold text-white">
                      <LiveCountdown
                        status={status}
                        statusLabel={get(liveStream, 'statusLabel')}
                        startTime={get(liveStream, 'startTime')}
                      />
                    </div>
                    <div className="flex items-center gap-x-pix-10">
                      {
                        // 需要报名
                        data.signRequire && (
                          <>
                            {
                              // 未报名
                              data.memberStatus === 'out' && (
                                <Button
                                  variant="contained"
                                  type="button"
                                  sx={{ borderRadius: '30px' }}
                                  onClick={openSigninDialog}
                                >
                                  {data.button}
                                </Button>
                              )
                            }
                            {
                              // 等待审核
                              data.memberStatus === 'pending' && (
                                <Button variant="outlined" type="button" sx={{ borderRadius: '30px' }}>
                                  {ts('statusPending')}
                                </Button>
                              )
                            }
                            {
                              // 已报名
                              data.memberStatus === 'in' && (
                                <Button variant="outlined" type="button" sx={{ borderRadius: '30px' }}>
                                  {ts('registered')}
                                </Button>
                              )
                            }
                          </>
                        )
                      }
                      <RemindButton
                        sx={{ borderRadius: '30px' }}
                        subscribed={isSubscribed(data.id)}
                        eventId={data.id}
                      >
                        {ts('remind')}
                      </RemindButton>
                    </div>
                  </>
                ))
              }
              {
                // 直播中
                status === liveStatus.living && (
                  <>
                    <div className="text-f.7 font-semibold text-white">{ts('liveNow')}</div>
                    {data.signRequire ? (
                      <>
                        {
                          // 未报名
                          data.memberStatus === 'out' && (
                            <Button
                              variant="contained"
                              type="button"
                              sx={{ borderRadius: '30px' }}
                              onClick={openSigninDialog}
                            >
                              {ts('viewNow')}
                            </Button>
                          )
                        }
                        {
                          // 等待审核
                          data.memberStatus === 'pending' && (
                            <Button variant="outlined" type="button" sx={{ borderRadius: '30px' }}>
                              {ts('statusPending')}
                            </Button>
                          )
                        }
                        {
                          // 已报名
                          data.memberStatus === 'in' && (
                            <Button
                              variant="contained"
                              type="button"
                              sx={{ borderRadius: '30px' }}
                              onClick={playLiveHandle}
                            >
                              {ts('viewNow')}
                            </Button>
                          )
                        }
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        type="button"
                        sx={{ borderRadius: '30px' }}
                        onClick={playLiveHandle}
                      >
                        {ts('viewNow')}
                      </Button>
                    )}
                  </>
                )
              }
              {
                // 直播结束
                status === liveStatus.end && (
                  <>
                    <div className="text-f1.4 font-semibold text-white">{ts('end')}</div>
                    {data.liveStream.hasRecord && (
                      <Button
                        variant="contained"
                        type="button"
                        sx={{ borderRadius: '30px' }}
                        onClick={playRecordHandle}
                      >
                        {ts('playback2')}
                      </Button>
                    )}
                  </>
                )
              }
            </div>
          </>
        )}
      </section>
      <TcPlayerLayout />
    </>
  )
}
